import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Switch,
  Route
} from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import { detect } from 'detect-browser';
import { getAppInsights } from './components/TelemetryService';
import InsightsProvider from './components/InsightsProvider';

// Pages
import './App.css';
import CurrentInitiativeLayout from './pages/layouts/CurrentInitiativeLayout';
import PastInitiativeLayout from './pages/layouts/PastInitiativeLayout';
import AdminLayout from './pages/layouts/AdminLayout';
import InitiativeCardLayout from './pages/layouts/InitiativeCardLayout';
import InitiativeBackLayout from './pages/layouts/InitiativeBackLayout';
import SearchLayout from './pages/layouts/SearchLayout';
import ErrorLayout from './pages/layouts/ErrorLayout';
import MyInitiatives from './pages/MyInitiatives';
import AdminReviewerDashboard from './pages/AdminReviewerDashboard';
import AdminApproversDashboard from './pages/AdminApproversDashboard';
import AdminWorkstreamDashboard from './pages/AdminWorkstreamDashboard';
import AdminSeeAllUserDashboard from './pages/AdminSeeAllUserDashboard';
import AdminUtilsDashboard from './pages/AdminUtilsDashboard';
import InitiativeCard from './pages/InitiativeCard';
import KPISetup from './pages/KPISetup';
import InitiativeDetails from './pages/InitiativeDetails';
import MilestonePlan from './pages/MilestonePlan';
import InitiativeValues from './pages/InitiativeValues';
import MyTeam from './pages/MyTeam';
import TransAlta from './pages/TransAlta';
import CreateInitiative from './pages/CreateInitiative';
import Error404 from './pages/Error404';
import SearchPage from './pages/SearchPage';
import ChangeTrackerLayout from './pages/layouts/ChangeTrackerLayout';
import ChangeTrackerView from './pages/ChangeTrackerView';
import ApproverLayout from './pages/layouts/ApproverLayout';
import ApproverView from './pages/ApproverView';
import ReviewerLayout from './pages/layouts/ReviewerLayout';
import ReviewerView from './pages/ReviewerView';
import UnsupportedBrowserLayout from './pages/layouts/UnsupportedBrowserLayout';
import UnsupportedBrowser from './pages/UnsupportedBrowser';
import ChangeLog from './pages/ChangeLog';
import KnowledgeBaseLayout from './pages/layouts/KnowledgeBaseLayout';
import KnowledgeBase from './pages/KnowledgeBase';
import EmbeddedReport from './pages/EmbeddedReport';

import { getData } from './utils/apiUtils';
import Colors from './utils/colors';
import { DropdownContext, PermissionsContext, BrowserContext, NotificationContext } from './utils/constants';
import { startListening, stopListening } from './utils/notificationListener';
import ErrorDialog, { ErrorProvider } from "./components/ErrorDialog";

function App() {
  const theme = useTheme();
  const [ errorTitle, setErrorTitle ] = useState('Oh No!');
  const [ errorDescription, setErrorDescription ] = useState('Something has gone wrong!');
  const [ errorRetry, setErrorRetry ] = useState(null);
  const [ showError, setShowError ] = useState(false);
  const [ dropdownList, setDropdownList ] = useState({ dropdowns: {}, dropdownValues: [] });
  const [ userPermissions, setUserPermissions ] = useState({ isAdmin:  false, isApprover: false, isWSL: false });

  const [ notification, setNotification ] = useState({});

  const [ unsupportedBrowser, setUnsupportedBrowser ] = useState(false);
  let appInsights = null;
  const browser = detect();

  const triggerError = ({title, description, onRetry}) => {
    setErrorTitle(title);
    setErrorDescription(description);
    setErrorRetry(() => onRetry);
    setShowError(true);
  };

  useEffect(() => {
    getData('/generaldata/dropdown-data', setDropdownList, () => {
      triggerError({
        title: 'Communication Error',
        description: 'We were unable to read app data from the server. Please try again',
        onRetry: () => window.location.reload()
      });
    });


    getData('/users/permissions', setUserPermissions, () => {
      triggerError({
        title: 'Communication Error',
        description: 'We were unable to read app data from the server. Please try again',
        onRetry: () => window.location.reload()
      });
    });

    if (browser.name === 'ie' && browser.version !== '11.0.0') {
        setUnsupportedBrowser(true);
    }

    getData('/generaldata/notification-settings',
      (connectionString ) => {
        connectionString && connectionString.url && startListening(connectionString,  onMessageReceived, onMessageError);
      },
      () => {
        console.log('Error getting the notification settings. The initiatives will not be updated dynamically.');
    });
  }, []);

  useEffect(() => {
    // returned function will be called on component unmount
    return async () => {
      await stopListening();
    }
  }, []);

  const onMessageReceived = (message) => {
    setNotification(message);
  };

  const onMessageError = (error) => {
    triggerError({
      title: 'Communication Error',
      description: 'There was an error receiving notification message from the server. Please try again',
      onRetry: () => window.location.reload()
    });
  }

  // Change the palette to have TransAlta colors.
  theme.palette.primary.main = Colors.primary;
  theme.palette.primary.dark = Colors.mouseDown;
  theme.palette.primary.light = Colors.mouseDown;
  theme.palette.secondary.main = Colors.secondary;
  theme.palette.secondary.light = Colors.secondary;
  theme.palette.secondary.dark = Colors.secondary;

  if (unsupportedBrowser) {
    return (
        <Router>
          <BrowserContext.Provider value={ browser }>
            <UnsupportedBrowserLayout><UnsupportedBrowser /></UnsupportedBrowserLayout>
          </BrowserContext.Provider>
        </Router>
      );
  }

  return (
    <Router>
      <ErrorProvider value={{ triggerError }}>
        <ErrorDialog
            title={errorTitle}
            description={errorDescription}
            onRetry={errorRetry}
            onClose={() => setShowError(false)}
            open={showError}
        />
        <InsightsProvider  after={() => { appInsights = getAppInsights() }}>
        <NotificationContext.Provider value={ notification }>
        <BrowserContext.Provider value={ browser }>
        <DropdownContext.Provider value={ dropdownList }>
        <PermissionsContext.Provider value={ userPermissions }>
          {/* A <Switch> looks through its children <Route>s and
            renders the first one that matches the current URL. */}
          <Switch>
            {/*Current initiative dashboards.*/}
            <Route exact path="/">
              <CurrentInitiativeLayout><MyInitiatives current={true}/></CurrentInitiativeLayout>
            </Route>
            <Route exact path="/ui/myteam">
              <CurrentInitiativeLayout><MyTeam current={true}/></CurrentInitiativeLayout>
            </Route>
            {/* <Route exact path="/ui/transalta">
              <CurrentInitiativeLayout><TransAlta current={true}/></CurrentInitiativeLayout>
            </Route> */}
            {/*Past initiative dashaboards.*/}
            <Route exact path="/ui/past/initiatives">
              <PastInitiativeLayout><MyInitiatives current={false}/></PastInitiativeLayout>
            </Route>
            <Route exact path="/ui/past/myteam">
              <PastInitiativeLayout><MyTeam current={false}/></PastInitiativeLayout>
            </Route>
            <Route exact path="/ui/past/transalta">
              <PastInitiativeLayout><TransAlta current={false}/></PastInitiativeLayout>
            </Route>
            <Route exact path="/ui/admin/reviewers">
              <AdminLayout><AdminReviewerDashboard /></AdminLayout>
            </Route>
            <Route exact path="/ui/admin/approvers">
              <AdminLayout><AdminApproversDashboard /></AdminLayout>
            </Route>
            <Route exact path="/ui/admin/seeallusers">
              <AdminLayout><AdminSeeAllUserDashboard title={'See all users'}
                                                     subtitle={'Users who can see all confidential users.'}
                                                     url={'/users/seeall/'}
              /></AdminLayout>
            </Route>
            <Route exact path="/ui/admin/administrators">
              <AdminLayout><AdminSeeAllUserDashboard title={'Administrators'} url={'/users/administrators/'}/>
              </AdminLayout>
            </Route>
            <Route exact path="/ui/admin/workstreams">
              <AdminLayout><AdminWorkstreamDashboard /></AdminLayout>
            </Route>
            <Route exact path="/ui/admin/utils">
              <AdminLayout><AdminUtilsDashboard /></AdminLayout>
            </Route>
            <Route exact path="/ui/createinitiative">
              <CreateInitiative />
            </Route>
            <Route exact path="/ui/errors/error404">
              <ErrorLayout><Error404 /></ErrorLayout>
            </Route>
            <Route exact path="/ui/search/:searchTerm">
              <SearchLayout><SearchPage /></SearchLayout>
            </Route>
            <Route exact path="/ui/initiativecard/kpis/:id">
              <InitiativeBackLayout><KPISetup /></InitiativeBackLayout>
            </Route>
            <Route exact path="/ui/initiativedetails/:id">
              <InitiativeBackLayout><InitiativeDetails /></InitiativeBackLayout>
            </Route>
            <Route exact path="/ui/initiativemilestones/:id">
              <MilestonePlan />
            </Route>
            <Route exact path="/ui/initiativevalues/:id">
              <InitiativeBackLayout><InitiativeValues /></InitiativeBackLayout>
            </Route>
            <Route exact path="/ui/initiativecard/:id">
              <InitiativeCardLayout><InitiativeCard /></InitiativeCardLayout>
            </Route>
            <Route exact path="/ui/changetracker-view">
              <ChangeTrackerLayout><ChangeTrackerView /></ChangeTrackerLayout>
            </Route>            
            <Route exact path="/ui/approver-view">
              <ApproverLayout><ApproverView /></ApproverLayout>
            </Route>
            <Route exact path="/ui/reviewer-view">
              <ReviewerLayout><ReviewerView /></ReviewerLayout>
            </Route>
            <Route exact path="/ui/change-log/:id">
              <InitiativeBackLayout><ChangeLog /></InitiativeBackLayout>
            </Route>
            <Route exact path="/ui/knowledge-base">
              <KnowledgeBaseLayout><KnowledgeBase /></KnowledgeBaseLayout>
            </Route>
            <Route exact path="/ui/transalta">
              <CurrentInitiativeLayout><EmbeddedReport></EmbeddedReport></CurrentInitiativeLayout>
            </Route>

          </Switch>
        </PermissionsContext.Provider>
        </DropdownContext.Provider>
        </BrowserContext.Provider>
        </NotificationContext.Provider>
        </InsightsProvider>
      </ErrorProvider>
    </Router>
  );
}

export default App;
